<script setup>
import { defineModel, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  disableSearch: {
    type: Boolean,
    default: false,
  },
  placeholderIcon: {
    type: String,
    default: 'i-lucide-plus',
  },
  placeholder: {
    type: String,
    default: '',
  },
  placeholderTrailingIcon: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  dropdownMaxHeight: {
    type: String,
    default: 'max-h-80',
  },
  disableDeselect: {
    type: Boolean,
    default: false,
  },
  dropUp: {
    type: Boolean,
    default: false,
  },
  align: {
    type: String,
    default: 'start',
    validator: value => ['start', 'end'].includes(value),
  },
  brandTrigger: {
    type: Boolean,
    default: false,
  },
  remoteSearch: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['search']);

const { t } = useI18n();
const selected = defineModel({
  type: Object,
  default: null,
});

const searchTerm = ref('');

watch(searchTerm, value => {
  if (props.remoteSearch) emit('search', value);
});

const searchResults = computed(() => {
  if (!props.options) return [];
  if (props.remoteSearch || !searchTerm.value.trim()) return props.options;

  const searchable = props.options.filter(option => !option.disabled);
  return picoSearch(searchable, searchTerm.value, ['name']);
});

const menuClass = computed(() => {
  const vertical = props.dropUp ? 'bottom-full mb-1 top-auto' : 'top-full mt-1';
  const horizontal = props.align === 'end' ? 'right-0' : 'left-0';
  return `${vertical} ${horizontal}`;
});

const selectedItem = computed(() => {
  if (!props.options) return null;
  if (!selected.value) return null;

  // there are cases where the selected value is an array
  const optionToSearch = Array.isArray(selected.value)
    ? selected.value[0]
    : selected.value;

  if (!optionToSearch) return null;
  // extract the selected item from the options array
  // this ensures that options like icon is also included
  return props.options.find(
    option => !option.disabled && option.id === optionToSearch.id
  );
});

const toggleSelected = option => {
  if (option.disabled) return;

  // Ensure that the `icon` prop is not included, icon is a VNode which has circular references
  // This causes an error when creating a clone using JSON.parse(JSON.stringify())
  const optionToToggle = {
    id: option.id,
    name: option.name,
  };

  if (selected.value && selected.value.id === optionToToggle.id) {
    if (!props.disableDeselect) selected.value = null;
  } else {
    selected.value = optionToToggle;
  }
};
</script>

<template>
  <DropdownContainer :menu-class="menuClass">
    <template #trigger="{ toggle }">
      <Button
        v-if="selectedItem"
        sm
        slate
        faded
        type="button"
        :icon="selectedItem.icon"
        :label="selectedItem.name"
        @click="toggle"
      />
      <Button
        v-else
        sm
        :slate="!brandTrigger"
        :faded="!brandTrigger"
        type="button"
        :trailing-icon="placeholderTrailingIcon"
        :class="
          brandTrigger
            ? 'bg-n-violet-3 text-n-violet-11 hover:bg-n-violet-4 border border-n-violet-6'
            : ''
        "
        @click="toggle"
      >
        <template #icon>
          <Icon
            :icon="placeholderIcon"
            :class="brandTrigger ? 'text-n-violet-11' : 'text-n-slate-11'"
          />
        </template>
        <span :class="brandTrigger ? 'text-n-violet-11' : 'text-n-slate-11'">{{
          placeholder || t('COMBOBOX.PLACEHOLDER')
        }}</span>
      </Button>
    </template>
    <DropdownBody class="min-w-56 !relative !top-auto !left-auto" strong>
      <div v-if="!disableSearch" class="relative">
        <Icon class="absolute size-4 left-2 top-2" icon="i-lucide-search" />
        <input
          v-model="searchTerm"
          autofocus
          class="p-1.5 pl-8 text-n-slate-11 bg-n-solid-2 rounded-lg w-full"
          :placeholder="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
        />
      </div>
      <DropdownSection :height="dropdownMaxHeight">
        <template v-if="searchResults.length">
          <template v-for="option in searchResults" :key="option.id">
            <li
              v-if="option.disabled"
              class="px-2 py-1.5 text-xs font-medium text-n-slate-10 select-none"
            >
              {{ option.name }}
            </li>
            <DropdownItem
              v-else
              :icon="option.icon"
              @click="toggleSelected(option)"
            >
              <template #label>
                <div class="flex flex-col">
                  <div class="flex items-center gap-1">
                    {{ option.name }}
                    <Icon
                      v-if="selectedItem && selectedItem.id === option.id"
                      icon="i-lucide-check"
                      class="bg-n-woot-text pointer-events-none"
                    />
                  </div>
                  <span
                    v-if="option.description"
                    class="text-xs text-n-slate-10"
                  >
                    {{ option.description }}
                  </span>
                </div>
              </template>
            </DropdownItem>
          </template>
        </template>
        <template v-else-if="searchTerm">
          <DropdownItem disabled>
            {{ t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm: searchTerm }) }}
          </DropdownItem>
        </template>
        <template v-else>
          <DropdownItem disabled>
            {{ t('COMBOBOX.EMPTY_STATE') }}
          </DropdownItem>
        </template>
      </DropdownSection>
    </DropdownBody>
  </DropdownContainer>
</template>
